import React, { Fragment, useEffect } from "react";

import ShakeIcon from "@common/assets/images/details-pane-icons/shake-sdk-icon.svg";
import CodeIcon from "@common/assets/images/FTUX/add-app-code.svg";
import CodeBlock from "@common/components/CodeBlock/CodeBlock";
import Heading from "@common/components/Heading/Heading";
import PageWrap from "@common/components/PageWrap/PageWrap";
import Paragraph from "@common/components/Paragraph/Paragraph";
import displayToast from "@common/components/Toast/displayToast";
import Toast from "@common/components/Toast/Toast";
import { PlatformName } from "@common/models";
import { docLink } from "@common/util/docs";
import { helpCenterArticles } from "@common/util/helpCenterArticles";
import { showIntercomArticle } from "@common/util/intercom";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";

import {
  SdkSetupStep,
  useAndroidSdkSetup,
  useFlutterSdkSetup,
  useIosSdkSetup,
  useReactNativeSdkSetup,
  useWebSdkSetup,
} from "./helpers/sdkSetupSteps";
import * as Styled from "./SDKSetupGuide.styles";
import { SetupStep } from "../SetupStep/SetupStep";

const ADD_SDK_TOAST = "add-sdk";

export function SdkSetupSteps() {
  const { selectedApp } = useAppSelectionContext();

  const apiKey = selectedApp?.api_keys[0]?.key ?? "your-api-key";
  const platform = selectedApp?.platform;
  const latestVersion = selectedApp?.platform?.latest_version ?? "";

  const androidSdkSetup = useAndroidSdkSetup(apiKey, latestVersion);
  const iosSdkSetup = useIosSdkSetup(apiKey);
  const reactNativeSdkSetup = useReactNativeSdkSetup(platform, apiKey);
  const flutterSdkSetup = useFlutterSdkSetup(apiKey);
  const webSdkSetup = useWebSdkSetup(apiKey);

  switch (selectedApp?.platform?.name) {
    case PlatformName.ANDROID:
      return <SdkSetupStepsView sdkSetupSteps={androidSdkSetup.steps} />;
    case PlatformName.IOS:
      return <SdkSetupStepsView sdkSetupSteps={iosSdkSetup.steps} />;
    case PlatformName.FLUTTER:
      return <SdkSetupStepsView sdkSetupSteps={flutterSdkSetup.steps} />;
    case PlatformName.REACT_NATIVE:
      return <SdkSetupStepsView sdkSetupSteps={reactNativeSdkSetup.steps} />;
    case PlatformName.WEB:
      return <SdkSetupStepsView sdkSetupSteps={webSdkSetup.steps} />;
    default:
      return null;
  }
}

interface Props {
  sdkSetupSteps: SdkSetupStep[];
}

const SdkSetupStepsView = ({ sdkSetupSteps }: Props) => {
  const { selectedApp } = useAppSelectionContext();
  const appName = selectedApp?.name ?? "your app";

  useEffect(() => {
    displayToast({
      icon: CodeIcon,
      containerId: ADD_SDK_TOAST,
      content: (
        <Fragment>
          If you feel adventurous and want to customize the installation, visit full&nbsp;
          {<a href={docLink(selectedApp?.platform.name).installation}>SDK documentation</a>}.
        </Fragment>
      ),
    });
  }, [appName, selectedApp?.platform.name]);

  return (
    <PageWrap
      key={appName}
      style={{ overflow: "scroll", height: "100%", display: "flex", flex: 1, justifyContent: "center" }}
    >
      <Styled.Container>
        <Heading
          className={"center"}
          heading1
          marginBottom={58}
        >
          {`Add`}&nbsp;
          <span>
            <img
              alt="sdk-icon"
              src={ShakeIcon}
            />
          </span>
          {`SDK to ${appName}`}
        </Heading>

        <Styled.Helper marginBottom={8}>
          Now listening to {appName} {selectedApp?.platform?.name} app. When you finish the installation, your app will
          ping our servers and you will be automatically taken to the next step.
        </Styled.Helper>
        <Styled.Helper marginBottom={38}>
          {"Stuck? Don’t wait more than a minute. Check the "}
          <a onClick={() => showIntercomArticle(helpCenterArticles.sdkSetupMisconfigurations)}>
            {"8 most common SDK misconfigurations and solutions"}
          </a>
          {", and "}
          <a href="javascript:window.Intercom?.('showNewMessage')">{"contact our support"}</a>
          {" if nothing helps."}
        </Styled.Helper>

        {sdkSetupSteps.map((step, index) => (
          <div key={step.codeBlock[0].title}>
            <SetupStep
              number={index + 1}
              title={step.title}
            />
            <Styled.StepContent>
              {step.subtitle && (
                <Paragraph
                  lineHeight={26}
                  marginBottom={12}
                >
                  {step.subtitle}
                </Paragraph>
              )}
              <CodeBlock
                tabController={step.controller}
                tabs={step.codeBlock}
              />
              {step.footer && (
                <Paragraph
                  lineHeight={26}
                  marginTop={12}
                  marginBottom={60}
                >
                  {step.footer}
                </Paragraph>
              )}
            </Styled.StepContent>
          </div>
        ))}
        <Toast
          containerId={ADD_SDK_TOAST}
          autoClose={false}
        />
      </Styled.Container>
    </PageWrap>
  );
};
